// src/features/task/TaskStatusControl.tsx
// Status control driven by GetAvailableTransitions: offers only the transitions
// that are legal from the task's current status. Falls back to the full status
// list if the type defines no transition rules (the RPC returns all statuses).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Alert,
  Badge,
} from '@react/components';
import {
  formatEnumLabel,
  getStatusMapping,
  taskStatusMap,
  type BadgeVariant,
} from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskStatusControlProps {
  taskId: string;
  currentStatus: string;
  currentCustomStatus?: string;
  editable?: boolean;
  onChanged?: () => void;
}

function variantForCategory(category: string | undefined): BadgeVariant {
  return getStatusMapping(taskStatusMap, category).variant;
}

export const TaskStatusControl = observer(({
  taskId,
  currentStatus,
  currentCustomStatus,
  editable = true,
  onChanged,
}: TaskStatusControlProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [transitions, setTransitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [commentFor, setCommentFor] = useState<any | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await store.GetAvailableTransitions(taskId);
      setTransitions(res?.transitions || []);
    } catch (e) {
      setError(errMsg(e, t('task.transitions.loadError')));
      setTransitions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, currentStatus, currentCustomStatus]);

  const apply = async (transition: any) => {
    const toStatus: string = transition.to_status || '';
    const isBase = /^TASK_STATUS_/.test(toStatus);
    if (transition.requires_comment && commentFor?.to_status !== toStatus) {
      setCommentFor(transition);
      setComment('');
      return;
    }
    setPending(toStatus);
    setError(null);
    try {
      await store.ChangeTaskStatus(taskId, {
        status: (isBase ? toStatus : (transition.category || currentStatus)) as any,
        custom_status: isBase ? undefined : toStatus,
        comment: comment.trim() || undefined,
      } as any);
      setCommentFor(null);
      setComment('');
      await load();
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.transitions.changeError')));
    } finally {
      setPending(null);
    }
  };

  const currentLabel = currentCustomStatus
    || (currentStatus ? formatEnumLabel(currentStatus, 'TASK_STATUS_') : '--');

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('task.field.status')}
          </p>
          {currentStatus && (
            <Badge variant={variantForCategory(currentStatus)} size="sm">{currentLabel}</Badge>
          )}
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="h-8 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : !editable ? null : transitions.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('task.transitions.none')}</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{t('task.transitions.moveTo')}</p>
            <div className="flex flex-wrap gap-1.5">
              {transitions.map((tr: any, idx: number) => (
                <Button
                  key={tr.to_status || idx}
                  size="sm"
                  variant="secondary"
                  isLoading={pending === tr.to_status}
                  onClick={() => apply(tr)}
                >
                  {tr.display_name || formatEnumLabel(String(tr.to_status), 'TASK_STATUS_')}
                </Button>
              ))}
            </div>

            {commentFor && (
              <div className="space-y-2 rounded-md border border-border p-2">
                <p className="text-xs text-muted-foreground">
                  {t('task.transitions.commentRequired', { status: commentFor.display_name || commentFor.to_status })}
                </p>
                <input
                  type="text"
                  aria-label={t('task.transitions.comment')}
                  placeholder={t('task.transitions.comment')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                <div className="flex gap-1.5">
                  <Button size="sm" isLoading={pending === commentFor.to_status} disabled={!comment.trim()} onClick={() => apply(commentFor)}>
                    {t('task.transitions.confirm')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setCommentFor(null); setComment(''); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskStatusControl;
